import { describeSegment, type SegmentNode } from '@church/domain'
import {
  useCampuses,
  useDeleteList,
  useList,
  useListMembers,
  useAccess,
  useRefreshList,
  useSegmentPreview,
  useUpdateList,
} from '@church/data'
import {
  faArrowsRotate,
  faChevronDown,
  faDownload,
  faEllipsis,
  faFloppyDisk,
  faTrashCan,
} from '@church/ui-icons'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { SegmentBuilder } from '../components/SegmentBuilder.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import {
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Icon,
  IconButton,
  Input,
  ListTitle,
  PanelSection,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'

/**
 * A saved list: rules on the left, live count and members on the right.
 *
 * The count comes from `POST /lists/preview` running the *edited* tree, so
 * what the number describes is exactly what Save would store — the acceptance
 * criterion for this phase is that a builder never shows a count the server
 * would disagree with, and the surest way to achieve that is to let the server
 * do the counting.
 */
export const Route = createFileRoute('/_authed/_shell/lists/$id')({
  component: ListDetailScreen,
})

function ListDetailScreen() {
  const { id } = Route.useParams()
  const { data: list, isPending, isError } = useList(id)
  const access = useAccess()
  const update = useUpdateList(id)
  const refresh = useRefreshList()
  const remove = useDeleteList()
  const navigate = useNavigate()
  const [options, setOptions] = useState(false)

  const [rules, setRules] = useState<SegmentNode | null>(null)
  const [name, setName] = useState('')

  // Adopt the server's copy once, and again whenever a save returns.
  useEffect(() => {
    if (list) {
      setRules(list.rules as SegmentNode)
      setName(list.name)
    }
  }, [list])

  const editable = access.has('people', 'editor')
  const working = rules ?? { all: [] }
  const preview = useSegmentPreview(working)
  const dirty = list ? JSON.stringify(working) !== JSON.stringify(list.rules) : false

  useBreadcrumbs([{ label: list?.name ?? 'List' }])

  if (isPending) return <Spinner />
  if (isError || !list) {
    return <EmptyState title="List not found" hint="It may have been deleted." />
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          {editable ? (
            <Input
              className="max-w-md text-lg font-bold"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : (
            <h1 className="sr-only">{list.name}</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editable && dirty && (
            <span className="text-xs text-content-subtle">Unsaved changes</span>
          )}
          {editable && (
            <Button
              icon={faFloppyDisk}
              disabled={update.isPending || (!dirty && name === list.name)}
              onClick={() => update.mutate({ name: name.trim(), rules: working })}
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          )}
          <IconButton
            icon={faEllipsis}
            label="List options"
            variant="secondary"
            onClick={() => setOptions(true)}
          />
        </div>
      </div>

      <SidePanel
        open={options}
        onOpenChange={(next) => {
          if (!next) setOptions(false)
        }}
        title={list.name}
        footer={
          access.has('people', 'administrator') ? (
            <ConfirmButton
              label="Delete list"
              icon={faTrashCan}
              variant="quietDanger"
              className="-ml-4"
              title={`Delete “${list.name}”?`}
              description="The rules are deleted. Nobody is removed from the directory."
              confirmLabel="Delete list"
              onConfirm={() =>
                remove.mutate(list.id, { onSuccess: () => void navigate({ to: '/lists' }) })
              }
            />
          ) : undefined
        }
      >
        <PanelSection title="Export" hint="Everyone the saved rules match, as a spreadsheet.">
          <a
            href={`/api/v1/exports/lists/${id}`}
            className="inline-flex items-center gap-2 rounded-lg border border-border-default bg-surface-raised px-4 py-2 text-sm font-medium text-content-default hover:bg-surface-sunken"
          >
            <Icon icon={faDownload} />
            CSV
          </a>
        </PanelSection>
        {editable && (
          <PanelSection
            title="Saved count"
            hint={
              list.refreshedAt
                ? `Counted ${new Date(list.refreshedAt).toLocaleString()}. The lists page shows this number.`
                : 'Never counted. The lists page shows this number.'
            }
          >
            <Button
              variant="secondary"
              icon={faArrowsRotate}
              disabled={refresh.isPending}
              onClick={() => refresh.mutate(list.id)}
            >
              {refresh.isPending ? 'Counting…' : 'Count again'}
            </Button>
          </PanelSection>
        )}
        <ErrorNote error={refresh.error ?? remove.error} />
      </SidePanel>

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card className="space-y-3 p-4">
          <h2 className="text-sm font-semibold text-content-default">Rules</h2>
          {editable ? (
            <SegmentBuilder value={working} onChange={setRules} />
          ) : (
            <p className="text-sm text-content-muted">{describeSegment(working)}</p>
          )}
          <ErrorNote error={update.error ?? preview.error} />
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-content-subtle">Matches now</p>
            <p className="text-3xl font-bold tabular-nums text-content-default">
              {preview.isLoading || preview.isStale ? '…' : (preview.count ?? '—')}
            </p>
            <p className="text-xs text-content-muted">
              {preview.summary || describeSegment(working)}
            </p>
            <p className="mt-2 text-xs text-content-subtle">
              Counted in your campus scope, live — not from the saved total.
            </p>
          </Card>

          <MembersCard listId={id} dirty={dirty} />
        </div>
      </div>
    </div>
  )
}

function MembersCard({ listId, dirty }: { listId: string; dirty: boolean }) {
  const { members, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useListMembers(listId)
  const { data: campuses } = useCampuses()

  return (
    <Card bleed>
      <ListTitle title="Members">
        {dirty && (
          <span className="pr-2 text-xs text-warning-content">Showing the saved rules</span>
        )}
      </ListTitle>
      <div className="p-4">
        {isLoading ? (
          <Spinner />
        ) : members.length === 0 ? (
          <p className="text-sm text-content-muted">Nobody matches yet.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {members.map((person) => (
              <li key={person.id} className="flex items-center justify-between gap-3 py-2">
                <Link
                  to="/people/$id"
                  params={{ id: person.id }}
                  className="truncate text-sm text-content-default hover:underline"
                >
                  {person.lastName}, {person.nickname ?? person.firstName}
                </Link>
                <span className="shrink-0 text-xs text-content-muted">
                  {campuses?.find((c) => c.id === person.campusId)?.name ?? person.membershipStatus}
                </span>
              </li>
            ))}
          </ul>
        )}

        {hasNextPage && (
          <Button
            variant="secondary"
            icon={faChevronDown}
            className="mt-3"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'More'}
          </Button>
        )}
      </div>
    </Card>
  )
}
